import React, { useMemo, useState } from 'react';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Stack, Accordion, Typography, AccordionDetails, AccordionSummary, useMediaQuery } from '@mui/material';

import { HealthCategoryId, IntakeStatusCustomSteps } from '@shared/patient-intake-status';

import { determineIfStepIsDisabled } from '../utils';
import { BaseStepsSection } from '../BaseStepsSection';
import { usePatientIntakeStatusContext } from '../hooks';
import { HealthCategoriesSection, PastHealthCategoriesSection } from '../HealthCategoriesSection';
import { SummaryRings } from './SummaryRings.tsx';
import { CHECKUP } from '../../../../utils/constants.js';

type AreAllCustomStepsCompletedParams = {
  customSteps: IntakeStatusCustomSteps | null;
  activeHealthCategoryIds: HealthCategoryId[] | null;
};

const areAllCustomStepsCompleted = ({ customSteps, activeHealthCategoryIds }: AreAllCustomStepsCompletedParams) =>
  activeHealthCategoryIds?.every((healthCategoryId) =>
    Object.values(customSteps?.[healthCategoryId] || {}).every(
      (step) => step.isDisabled || step.currentStatus.status === 'COMPLETED',
    ),
  );

type GetTitleTextParams = {
  isHealthcareProvider: boolean;
  activeCategories: string[];
};

const getTitleText = ({ isHealthcareProvider, activeCategories }: GetTitleTextParams) => {
  if (!isHealthcareProvider) {
    return `Your ${activeCategories.length === 1 ? `${activeCategories[0]} Intake` : 'Intake'} Status`;
  }

  if (activeCategories.length <= 1) {
    return `Patient Intake Status - ${activeCategories[0] || 'No active form'}`;
  }

  return `Patient Intake Status - ${activeCategories.length} active categories`;
};

const Title: React.FC = () => {
  const { isHealthcareProvider, healthCategories } = usePatientIntakeStatusContext();

  const activeCategories = Object.keys(healthCategories || {}).filter(
    (category) => healthCategories?.[category as HealthCategoryId] === 'AWAITING_REVIEW',
  );

  const titleText = getTitleText({ isHealthcareProvider, activeCategories });

  return (
    <Typography variant="h6" sx={{ textTransform: 'capitalize', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
      {titleText}
    </Typography>
  );
};

const Description: React.FC = () => {
  const { isHealthcareProvider, healthCategories } = usePatientIntakeStatusContext();

  if (isHealthcareProvider) {
    return null;
  }

  const activeCategories = Object.keys(healthCategories || {}).filter(
    (category) => healthCategories?.[category as HealthCategoryId] === 'AWAITING_REVIEW',
  );

  if (activeCategories.length === 0) {
    return <Typography variant="body2">You don't have any active forms at the moment</Typography>;
  }

  if (activeCategories.length === 1 && activeCategories[0] === CHECKUP) {
    return <Typography variant="body2">Complete these steps to speed up your checkup process</Typography>;
  }

  return <Typography variant="body2">Complete these steps to speed up your intake process</Typography>;
};

export const PatientIntakeStatusContent: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const { isHealthcareProvider, baseSteps, customSteps, activeHealthCategoryIds } = usePatientIntakeStatusContext();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const handleToggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  const areAllStepsCompleted = useMemo(
    () =>
      Object.values(baseSteps || {}).every(
        (step) => determineIfStepIsDisabled({ step }) || step.currentStatus.status === 'COMPLETED',
      ) && areAllCustomStepsCompleted({ customSteps, activeHealthCategoryIds }),
    [baseSteps, customSteps],
  );

  if (!isHealthcareProvider && areAllStepsCompleted) {
    return null;
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleToggleExpanded}
      sx={{
        borderRadius: '0.5rem',
        borderColor: 'divider',
        borderWidth: '1px',
        borderStyle: 'solid',
      }}
    >
      <AccordionSummary component="div">
        <Stack direction="column" alignItems="flex-start" width="100%" py={1} gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            width="100%"
            gap={1}
          >
            <Title />

            <Stack direction="row" alignItems="center" justifyContent="flex-start" gap={1}>
              {!isMobile && <SummaryRings />}
              <KeyboardArrowDown
                style={{
                  transition: 'transform 0.1s ease-in-out',
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  opacity: 0.2,
                }}
              />
            </Stack>
          </Stack>

          {isMobile && <SummaryRings alignSelf="center" />}

          <Description />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <BaseStepsSection />

        <Stack direction="column" gap={2}>
          <HealthCategoriesSection />

          <PastHealthCategoriesSection />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};
